import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { FormError } from "@/components/FormError";
import { tenantsApi } from "@/api/endpoints/tenants";
import { authStorage } from "@/lib/auth-storage";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";

const schema = z.object({
  name: z.string().min(1, "Workspace name is required."),
  slug: z
    .string()
    .min(3, "URL must be 3-32 characters.")
    .max(32, "URL must be 3-32 characters.")
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])$/, "Lowercase letters, numbers, hyphens only."),
});
type FormValues = z.infer<typeof schema>;

export function OnboardingFirstTenantPage(): JSX.Element {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ defaultValues: { name: "", slug: "" } });

  const create = useMutation({
    mutationFn: async (v: FormValues) => {
      const t = await tenantsApi.create(schema.parse(v));
      const sw = await tenantsApi.switch({ tenant_id: t.tenant.id });
      authStorage.set(sw.access_token);
      return t.tenant.id;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      navigate({ to: "/dashboard" });
    },
    onError: (err) => {
      if (err instanceof ApiError) setServerError(translateError(err.code, err.message));
      else setServerError(translateError("COMMON_UNKNOWN"));
    },
  });

  return (
    <div className="onboarding-shell">
      <div className="onboarding-card" data-testid="onboarding-card">
        <div>
          <h1>Set up your workspace</h1>
          <p>
            A workspace holds your knowledge, conversations, and team. You can rename it any
            time.
          </p>
        </div>
        <form onSubmit={handleSubmit((v) => { setServerError(null); create.mutate(v); })} noValidate>
          <div className="field-group" style={{ marginBottom: 16 }}>
            <label htmlFor="name">Workspace name</label>
            <input
              id="name"
              className="field-input"
              placeholder="Kiwi Academy"
              data-testid="onboarding-name"
              {...register("name", { required: "Name is required." })}
            />
            <FormError message={errors.name?.message} />
          </div>
          <div className="field-group" style={{ marginBottom: 16 }}>
            <label htmlFor="slug">Workspace URL</label>
            <input
              id="slug"
              className="field-input mono"
              placeholder="kiwi-academy"
              data-testid="onboarding-slug"
              {...register("slug", { required: "URL is required." })}
            />
            <span className="help">
              chat.yai.nz/<span className="mono" style={{ color: "var(--text-1)" }}>your-slug</span>
            </span>
            <FormError message={errors.slug?.message} />
          </div>
          <FormError message={serverError} />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={isSubmitting || create.isPending}
              data-testid="onboarding-submit"
            >
              {create.isPending ? "Creating…" : "Create workspace"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
